import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiCreditCard,
  FiInfo,
  FiLock,
} from "react-icons/fi";
import { FaStripe, FaUniversity } from "react-icons/fa";
import StripePaymentForm from "../components/StripePaymentForm";
import { API_BASE_URL, formatCents } from "../services/api";

/**
 * Customer-facing invoice payment page (no login).
 *
 * Reached through the share link the app emails out — `/pay/:token`, where the
 * token is an opaque 60-day share token. Everything on this page comes from the
 * three public endpoints below; nothing here is authenticated and nothing here
 * ever collects a card number.
 */

const money = (cents) => formatCents(cents, { withDecimals: true });

const longDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

/** The company address arrives as a structured object; render it as lines. */
const addressLines = (address) => {
  if (!address) return [];
  if (typeof address === "string") return address.split("\n").filter(Boolean);
  const cityLine = [address.city, address.state, address.postalCode]
    .filter(Boolean)
    .join(", ")
    .replace(/, (\d)/, " $1");
  return [address.street, address.line2, cityLine, address.country].filter(
    Boolean,
  );
};

const errorMessage = (error, fallback) =>
  (error && error.response && error.response.data
    ? error.response.data.message
    : null) || fallback;

/** The first payment option the company has actually switched on. */
const firstEnabled = (options) =>
  ["stripe", "bankTransfer", "zelle"].find(
    (key) => options && options[key] && options[key].enabled,
  ) || null;

function Spinner({ className = "" }) {
  return (
    <span
      className={`inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent ${className}`}
      aria-hidden="true"
    />
  );
}

function SandboxBanner() {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      <FiAlertTriangle className="mt-0.5 flex-shrink-0" size={18} />
      <p>
        <span className="font-semibold">Sandbox mode</span> — no real charge
        will be made. This invoice is being settled in demo mode so you can see
        the whole flow end to end.
      </p>
    </div>
  );
}

/** Selectable payment-method tile. */
function MethodTile({ active, onClick, icon, label, hint }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-colors ${
        active
          ? "border-primary bg-primary/5 ring-1 ring-primary"
          : "border-line bg-white hover:border-primary/40"
      }`}
    >
      <span className="flex-shrink-0">{icon}</span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-navy">{label}</span>
        {hint && (
          <span className="block truncate text-xs text-slate-500">{hint}</span>
        )}
      </span>
    </button>
  );
}

export default function PayInvoice() {
  const { token } = useParams();

  const [status, setStatus] = useState("loading"); // loading | ready | invalid
  const [data, setData] = useState(null);
  const [method, setMethod] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [result, setResult] = useState(null); // completed payment
  const [liveIntent, setLiveIntent] = useState(null); // live Stripe SetupIntent-style handoff

  const invoice = data && data.invoice;
  const company = data && data.company;
  const options = data && data.options;

  // After a payment the server's fresh balance supersedes the loaded invoice.
  const pending = Boolean(result && result.pending);
  const balanceCents =
    result && !pending
      ? result.balanceCents
      : invoice
        ? invoice.balanceCents
        : 0;
  const alreadyPaid = !!invoice && invoice.balanceCents <= 0;
  const sandbox = !!(
    options &&
    ((options.stripe && options.stripe.sandbox) || false)
  );

  const confirmPayment = useCallback(
    async (paymentIntentId, amountCents) => {
      const res = await axios.post(`${API_BASE_URL}/pay/${token}/confirm`, {
        paymentIntentId,
      });
      setResult({ ...res.data, amountCents });
    },
    [token],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/pay/${token}`);
        if (cancelled) return;
        setData(res.data);
        setMethod(firstEnabled(res.data.options));
        setStatus("ready");
      } catch (err) {
        if (!cancelled) setStatus("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const enabled = useMemo(
    () => ({
      stripe: !!(options && options.stripe && options.stripe.enabled),

      bankTransfer: !!(
        options &&
        options.bankTransfer &&
        options.bankTransfer.enabled
      ),
      zelle: !!(options && options.zelle && options.zelle.enabled),
    }),
    [options],
  );

  const anyOnline = enabled.stripe;

  async function pay(provider) {
    if (busy) return;
    setBusy(true);
    setNotice("");
    setLiveIntent(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/pay/${token}/checkout`, {
        provider,
      });
      const intent = res.data.paymentIntent;

      // Live Stripe: the card is collected by Stripe's own secure form, which
      // this page deliberately does not stand in for. Never collect a PAN here.
      if (!intent.sandbox && provider === "stripe") {
        setLiveIntent(intent);
        return;
      }

      await confirmPayment(intent.id, intent.amountCents);
    } catch (err) {
      setNotice(errorMessage(err, "We could not start your payment."));
    } finally {
      setBusy(false);
    }
  }

  // ------------------------------------------------------------------ states

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <div className="flex flex-col items-center gap-3 text-slate-500">
          <Spinner className="h-8 w-8 border-4 text-primary" />
          <p className="text-sm">Loading your invoice…</p>
        </div>
      </div>
    );
  }

  if (status === "invalid") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface px-4 py-12">
        <div className="card w-full max-w-md p-8 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-500">
            <FiAlertTriangle size={22} />
          </div>
          <h1 className="mt-4 text-xl font-bold text-navy">
            This payment link is no longer valid
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            The link may have expired, or the invoice may have been withdrawn.
            Please contact the sender for an up-to-date payment link.
          </p>
        </div>
      </div>
    );
  }

  const paidView = result || alreadyPaid;

  return (
    <div className="min-h-screen bg-surface px-4 py-8 sm:py-12">
      <div className="mx-auto w-full max-w-3xl">
        {/* Letterhead */}
        <div className="card p-6 sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-xl font-bold text-navy sm:text-2xl">
                {company.name}
              </h1>
              <div className="mt-1 space-y-0.5 text-sm text-slate-600">
                {addressLines(company.address).map((line) => (
                  <p key={line}>{line}</p>
                ))}
                {company.phone && <p>{company.phone}</p>}
                {company.email && <p>{company.email}</p>}
              </div>
            </div>
            <div className="sm:text-right">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Invoice
              </p>
              <p className="text-lg font-bold text-navy">{invoice.number}</p>
              <p className="mt-1 text-sm text-slate-600">
                Issued {longDate(invoice.issueDate)}
              </p>
              <p className="text-sm text-slate-600">
                Due {longDate(invoice.dueDate)}
              </p>
              <p className="mt-1 text-sm text-slate-600">
                Billed to{" "}
                <span className="font-semibold text-navy">
                  {invoice.customerName}
                </span>
              </p>
            </div>
          </div>

          {/* Line items */}
          <div className="mt-6 -mx-6 overflow-x-auto sm:-mx-8">
            <div className="inline-block min-w-full px-6 align-middle sm:px-8">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-4">Description</th>
                    <th className="py-2 px-4 text-right">Qty</th>
                    <th className="py-2 px-4 text-right">Unit price</th>
                    <th className="py-2 pl-4 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {(invoice.lines || []).map((line, i) => (
                    <tr key={`${line.description}-${i}`}>
                      <td className="py-3 pr-4 text-navy">
                        {line.description}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-600">
                        {line.quantity}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-600">
                        {money(line.unitPriceCents)}
                      </td>
                      <td className="py-3 pl-4 text-right font-semibold text-navy">
                        {money(line.totalCents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="mt-6 flex justify-end border-t border-line pt-5">
            <dl className="w-full max-w-xs space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-slate-600">Invoice total</dt>
                <dd className="font-semibold text-navy">
                  {money(invoice.totalCents)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-600">Paid to date</dt>
                <dd className="font-semibold text-navy">
                  {money(invoice.totalCents - balanceCents)}
                </dd>
              </div>
              <div className="flex items-baseline justify-between border-t border-line pt-3">
                <dt className="text-sm font-semibold text-navy">Amount Due</dt>
                <dd className="text-2xl font-extrabold text-navy">
                  {money(balanceCents)}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Paid / success */}
        {paidView ? (
          <div
            className={`card mt-6 p-6 text-center sm:p-8 ${
              pending
                ? "border-blue-200 bg-blue-50"
                : "border-emerald-200 bg-emerald-50"
            }`}
          >
            <div
              className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${
                pending
                  ? "bg-blue-100 text-blue-600"
                  : "bg-emerald-100 text-emerald-600"
              }`}
            >
              <FiCheckCircle size={24} />
            </div>
            <h2
              className={`mt-4 text-lg font-bold ${
                pending ? "text-blue-900" : "text-emerald-900"
              }`}
            >
              {pending
                ? "Payment accepted — thank you"
                : balanceCents === 0
                  ? "Paid in full — thank you"
                  : "Payment received — thank you"}
            </h2>
            {pending ? (
              <p className="mt-2 text-sm text-blue-800">
                {result.message ||
                  "Your bank payment is on its way. Bank payments take a few working days to clear, and this invoice is marked paid once the money arrives."}
              </p>
            ) : result ? (
              <>
                <p className="mt-2 text-sm text-emerald-800">
                  We received your payment of{" "}
                  <span className="font-semibold">
                    {money(result.amountCents)}
                  </span>{" "}
                  for {invoice.number}.
                </p>
                <p className="mt-1 text-sm text-emerald-800">
                  Remaining balance:{" "}
                  <span className="font-semibold">
                    {money(result.balanceCents)}
                  </span>
                </p>
                {result.sandbox && (
                  <p className="mt-3 text-xs font-semibold text-amber-700">
                    Sandbox mode — this was a demo payment and no real charge
                    was made.
                  </p>
                )}
              </>
            ) : (
              <p className="mt-2 text-sm text-emerald-800">
                Invoice {invoice.number} has already been settled in full. There
                is nothing left to pay.
              </p>
            )}
            <p className="mt-4 text-xs text-emerald-700">
              Questions? Contact {company.name}
              {company.email ? ` at ${company.email}` : ""}.
            </p>
          </div>
        ) : (
          <div className="card mt-6 p-6 sm:p-8">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
              How would you like to pay?
            </h2>

            {sandbox && anyOnline && (
              <div className="mt-4">
                <SandboxBanner />
              </div>
            )}

            {notice && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {notice}
              </div>
            )}

            {!method && (
              <p className="mt-4 text-sm text-slate-600">
                {company.name} has not enabled online payments for this invoice
                yet. Please contact them
                {company.email ? ` at ${company.email}` : ""} to arrange
                payment.
              </p>
            )}

            {method && (
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {enabled.stripe && (
                  <MethodTile
                    active={method === "stripe"}
                    onClick={() => setMethod("stripe")}
                    icon={<FaStripe size={32} className="text-[#635BFF]" />}
                    label="Card or bank"
                    hint="Visa, Mastercard, Amex or a bank account"
                  />
                )}
                {enabled.bankTransfer && (
                  <MethodTile
                    active={method === "bankTransfer"}
                    onClick={() => setMethod("bankTransfer")}
                    icon={<FaUniversity size={20} className="text-navy" />}
                    label="Bank Transfer"
                    hint="Pay directly from your bank"
                  />
                )}
                {enabled.zelle && (
                  <MethodTile
                    active={method === "zelle"}
                    onClick={() => setMethod("zelle")}
                    icon={
                      <span className="text-lg font-extrabold text-[#6D1ED4]">
                        Z
                      </span>
                    }
                    label="Zelle"
                    hint="Send from your banking app"
                  />
                )}
              </div>
            )}

            {/* Card */}
            {method === "stripe" && (
              <div className="mt-6">
                {liveIntent ? (
                  <div className="rounded-xl border border-line bg-white p-4">
                    <StripePaymentForm
                      clientSecret={liveIntent.clientSecret}
                      publishableKey={
                        (options.stripe && options.stripe.publishableKey) || ""
                      }
                      stripeAccount={liveIntent.stripeAccount || ""}
                      amountLabel={money(balanceCents)}
                      companyName={company.name}
                      onError={setNotice}
                      onPaid={() =>
                        confirmPayment(liveIntent.id, liveIntent.amountCents)
                      }
                    />
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => pay(method)}
                      disabled={busy}
                      className="btn-primary w-full disabled:opacity-60"
                    >
                      {busy ? (
                        <>
                          <Spinner /> Processing…
                        </>
                      ) : (
                        <>
                          <FiCreditCard size={16} /> Continue to pay{" "}
                          {money(balanceCents)}
                        </>
                      )}
                    </button>
                    <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-500">
                      <FiLock size={12} /> Card details are captured by Stripe —
                      never by this page.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Bank transfer — informational */}
            {method === "bankTransfer" && (
              <div className="mt-6 rounded-xl border border-line bg-surface p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-navy">
                  <FiInfo size={15} /> How to pay by bank transfer
                </p>
                <p className="mt-2 whitespace-pre-line text-sm text-slate-700">
                  {options.bankTransfer.instructions}
                </p>
                <p className="mt-3 text-xs text-slate-500">
                  Please quote invoice <strong>{invoice.number}</strong> as the
                  payment reference and send {money(balanceCents)}. Your balance
                  updates once {company.name} records the transfer.
                </p>
              </div>
            )}

            {/* Zelle — informational */}
            {method === "zelle" && (
              <div className="mt-6 rounded-xl border border-line bg-surface p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-navy">
                  <FiInfo size={15} /> How to pay with Zelle
                </p>
                <p className="mt-2 text-sm text-slate-700">
                  Open your banking app, choose Zelle, and send{" "}
                  <strong>{money(balanceCents)}</strong> to:
                </p>
                <p className="mt-2 break-all rounded-lg border border-line bg-white px-3 py-2 font-mono text-sm text-navy">
                  {options.zelle.recipient}
                </p>
                <p className="mt-3 text-xs text-slate-500">
                  Add invoice <strong>{invoice.number}</strong> to the memo so
                  the payment can be matched. Your balance updates once{" "}
                  {company.name} records it.
                </p>
              </div>
            )}
          </div>
        )}

        <p className="mt-6 text-center text-xs text-slate-500">
          Secure payment page · No account or sign-in required
        </p>
      </div>
    </div>
  );
}
